import { PageKey } from '@core/enums/page-keys.enum';
import {
  CheckinWorkflow,
  CheckinWorkflowStep,
  StepResult,
  WorkflowVariables,
} from './workflow-engine.types';

// one entry per nested `flow` result: which workflow is active, and where to resume its caller
interface ActiveCall {
  workflow: CheckinWorkflow<string>;
  callerStepId: string | null;
  returnStepId: string | null;
}

const MAX_AUTOMATIC_STEPS = 100;

export class WorkflowEngineService {
  private callStack: ActiveCall[] = [];
  private currentStepId: string | null = null;

  get hasActiveWorkflow(): boolean {
    return this.callStack.length > 0 && this.currentStepId !== null;
  }

  start(rootWorkflow: CheckinWorkflow<string>, variables: WorkflowVariables): PageKey | null {
    this.callStack = [{ workflow: rootWorkflow, callerStepId: null, returnStepId: null }];
    this.currentStepId = rootWorkflow.entry;
    return this.resolve(null, variables);
  }

  next(variables: WorkflowVariables): PageKey | null {
    return this.resolve(this.getCurrentStep().next(variables), variables);
  }

  resume(variables: WorkflowVariables): PageKey | null {
    if (this.callStack.length === 0) {
      return null;
    }
    return this.resolve(null, variables);
  }

  back(variables: WorkflowVariables): PageKey | null {
    const handler = this.getCurrentStep().back;
    return this.resolve(handler ? handler(variables) : { bubble: 'back' }, variables);
  }

  bypass(variables: WorkflowVariables): PageKey | null {
    const step = this.getCurrentStep();
    if (!step.bypass) {
      const workflow = this.callStack[this.callStack.length - 1].workflow;
      throw new Error(`Bypass is not supported at ${workflow.id}:${step.id}`);
    }
    return this.resolve(step.bypass(variables), variables);
  }

  exit(variables: WorkflowVariables): PageKey | null {
    const handler = this.getCurrentStep().exit;
    return this.resolve(handler ? handler(variables) : { bubble: 'exit' }, variables);
  }

  // Applies a result (if any), then keeps auto-advancing page-less steps until a page is found.
  private resolve(result: StepResult<string> | null, variables: WorkflowVariables): PageKey | null {
    let pending = result;

    for (let count = 0; count < MAX_AUTOMATIC_STEPS; count++) {
      if (pending) {
        if ('nextStep' in pending) {
          this.currentStepId = pending.nextStep;
        } else if ('flow' in pending) {
          this.callStack.push({
            workflow: pending.flow,
            callerStepId: this.currentStepId,
            returnStepId: pending.returnStep,
          });
          this.currentStepId = pending.flow.entry;
        } else if ('complete' in pending) {
          const finishedCall = this.callStack.pop();
          if (!finishedCall || this.callStack.length === 0) {
            this.currentStepId = null;
            return null;
          }
          if (finishedCall.returnStepId === null) {
            throw new Error(`Called workflow ${finishedCall.workflow.id} has no return step`);
          }
          this.currentStepId = finishedCall.returnStepId;
        } else {
          pending = this.bubbleToParent(pending.bubble, variables);
          if (pending === null) {
            return null;
          }
          continue;
        }
        pending = null;
      }

      const currentStep = this.getCurrentStep();
      if (currentStep.page) {
        return currentStep.page;
      }
      pending = currentStep.next(variables);
    }

    throw new Error(`Workflow exceeded ${MAX_AUTOMATIC_STEPS} automatic steps`);
  }

  // Pops the current call and forwards `back`/`exit` to whichever step called it.
  private bubbleToParent(
    action: 'back' | 'exit',
    variables: WorkflowVariables,
  ): StepResult<string> | null {
    const finishedCall = this.callStack.pop();
    if (!finishedCall || this.callStack.length === 0 || finishedCall.callerStepId === null) {
      this.currentStepId = null;
      return null;
    }

    this.currentStepId = finishedCall.callerStepId;
    const callerStep = this.getCurrentStep();
    const handler = action === 'back' ? callerStep.back : callerStep.exit;
    return handler ? handler(variables) : { bubble: action };
  }

  private getCurrentStep(): CheckinWorkflowStep<string> {
    const activeCall = this.callStack[this.callStack.length - 1];
    if (!activeCall || this.currentStepId === null) {
      throw new Error('Workflow has not started or has already completed');
    }

    const step = activeCall.workflow.steps[this.currentStepId];
    if (!step) {
      throw new Error(`Workflow ${activeCall.workflow.id} has no step: ${this.currentStepId}`);
    }

    return step;
  }
}
